import { create } from 'zustand';
import { tripsAPI } from '../services/api';
import type { BusinessTrip, TripCreate, TripUpdate } from '../services/api';

interface TripState {
  trips: BusinessTrip[];
  isLoading: boolean;
  error: string | null;
  fetchTrips: (params?: { project_id?: string; status?: string }) => Promise<void>;
  createTrip: (data: TripCreate) => Promise<BusinessTrip>;
  updateTrip: (id: string, data: TripUpdate) => Promise<BusinessTrip>;
  deleteTrip: (id: string) => Promise<void>;
  clearError: () => void;
}

function getErrorMessage(error: any): string {
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) return detail.map((e: any) => e.msg).join(', ');
    return String(detail);
  }
  return error?.message || 'An error occurred';
}

export const useTripStore = create<TripState>((set, get) => ({
  trips: [],
  isLoading: false,
  error: null,

  fetchTrips: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const trips = await tripsAPI.list(params);
      set({ trips, isLoading: false });
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false });
    }
  },

  createTrip: async (data) => {
    set({ error: null });
    try {
      const trip = await tripsAPI.create(data);
      set((state) => ({ trips: [trip, ...state.trips] }));
      return trip;
    } catch (error) {
      const msg = getErrorMessage(error);
      set({ error: msg });
      throw new Error(msg);
    }
  },

  updateTrip: async (id, data) => {
    set({ error: null });
    try {
      const updated = await tripsAPI.update(id, data);
      set((state) => ({
        trips: state.trips.map((t) => (t.id === id ? updated : t)),
      }));
      return updated;
    } catch (error) {
      const msg = getErrorMessage(error);
      set({ error: msg });
      throw new Error(msg);
    }
  },

  deleteTrip: async (id) => {
    set({ error: null });
    try {
      await tripsAPI.delete(id);
      set((state) => ({ trips: state.trips.filter((t) => t.id !== id) }));
    } catch (error) {
      const msg = getErrorMessage(error);
      set({ error: msg });
      throw new Error(msg);
    }
  },

  clearError: () => set({ error: null }),
}));
